import ceil from "lodash/ceil";
import { useTranslation } from "react-i18next";

import { logger } from "#app/monitoring/Logger";
import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { QuoteRequestLineOverthicknessDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLineOverthickness.api";
import { Icon } from "#shared/icons/Icon";
import { Alert } from "#shared/view/components/Alert";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import {
  computeVolumeM3,
  formatMaterialDimensions,
  getCncRawDimensions,
  getMaterialWeight,
  MaterialLayout,
  PartTypeSchema,
} from "shared";

type Props = {
  part: PartDto;
  materialLayout: MaterialLayout | null;
  useNesting: boolean;
  isInMultiPartNesting: boolean;
  overthickness: QuoteRequestLineOverthicknessDto;
};

const M3_IN_MM3 = 1_000_000_000;

export const NestedMaterialInformations = ({
  part,
  materialLayout,
  useNesting,
  isInMultiPartNesting,
  overthickness,
}: Props) => {
  const { t } = useTranslation();
  const isUsingNesting = useNesting || isInMultiPartNesting;

  const cncRawDimensionsResult = getCncRawDimensions({
    layout: { useNesting: isUsingNesting, materialLayout },
    part,
    overthickness,
  });

  if (!cncRawDimensionsResult.isValid) {
    logger.error(cncRawDimensionsResult.error);
    return (
      <Alert
        icon={<Icon name="alertTriangle" />}
        p="xs"
        variant="light"
        color="orange"
        title={t("qrl.nestedMaterialDimensionsError")}
        maw="500px"
      />
    );
  }

  const { height, width, length, tubeThickness } = cncRawDimensionsResult.rawDimensions;

  const nestedMaterialWeight = ceil(
    getMaterialWeight(
      computeVolumeM3({
        partType: part.type ?? PartTypeSchema.enum.OTHER,
        height,
        width,
        length,
        tubeThickness,
      }) * M3_IN_MM3,
      materialLayout?.density,
    ),
    3,
  );

  return (
    <Stack gap="xxxs">
      <Text>
        {t("qrl.nestedMaterialDimensions")} :{" "}
        <b>
          {formatMaterialDimensions(part.type, {
            height,
            width,
            length,
            tubeThickness,
          })}
        </b>
      </Text>
      {materialLayout && (!useNesting || isInMultiPartNesting) && (
        <Text>
          {t("qrl.nestedMaterialWeight")} : <b>{`${nestedMaterialWeight} kg`}</b>
        </Text>
      )}
    </Stack>
  );
};
